/**
 * 채팅 우측 "문서 미리보기" 패널 상태 (chat-doc-preview — 2026-06-30).
 *
 * chatStore 와 분리한 별도 store (additive — 기존 store 무변경). 한 번에 한 문서만 미리보기하므로
 * 전역 단일 entry. 방 전환 시 다른 방의 미리보기는 닫는다(closeIfNotRoom).
 */
import { create } from 'zustand';
import type { PreviewDoc } from '../lib/preview-doc';
import { PREVIEW_ZOOM_DEFAULT, PREVIEW_ZOOM_STEP, clampZoom } from '../lib/preview-fit';

export interface OpenPreview {
  /** 이 미리보기가 속한 방 — 방 전환 시 자동 닫힘 판단. */
  roomId: string;
  doc: PreviewDoc;
}

// chat-doc-preview-resize (2026-07-01) — 미리보기 패널 가로 폭. 좌측 경계 드래그로 조절, localStorage 영속.
export const CHAT_PREVIEW_MIN_WIDTH = 320;
export const CHAT_PREVIEW_MAX_WIDTH = 900;
const DEFAULT_PREVIEW_WIDTH = 480;
const LS_PREVIEW_WIDTH = 'chat.preview.width';

function clampPreviewWidth(w: number): number {
  return Math.min(CHAT_PREVIEW_MAX_WIDTH, Math.max(CHAT_PREVIEW_MIN_WIDTH, Math.round(w)));
}

function readPreviewWidth(): number {
  try {
    const v = Number(localStorage.getItem(LS_PREVIEW_WIDTH));
    return Number.isFinite(v) && v > 0 ? clampPreviewWidth(v) : DEFAULT_PREVIEW_WIDTH;
  } catch {
    return DEFAULT_PREVIEW_WIDTH;
  }
}

/**
 * chat-doc-preview-cols (2026-08-11) — 한 줄에 몇 장씩 볼지(1/2/4).
 *
 * PDF 는 페이지 canvas 를 세로 목록으로 쌓는데, 넓게 열었을 때 여백이 남아 한 화면에 한 장만 보였다.
 * 열 수를 바꿔 2장·4장을 나란히 본다. pdf 는 CSS grid, pptx 는 렌더러 zoom 으로 같은 폭을 만든다.
 */
export const CHAT_PREVIEW_COLS_OPTIONS = [1, 2, 4] as const;
export type ChatPreviewCols = (typeof CHAT_PREVIEW_COLS_OPTIONS)[number];
const LS_PREVIEW_COLS = 'chat.preview.cols';

function normalizeCols(v: unknown): ChatPreviewCols {
  const n = Number(v);
  return (CHAT_PREVIEW_COLS_OPTIONS as readonly number[]).includes(n) ? (n as ChatPreviewCols) : 1;
}

function readCols(): ChatPreviewCols {
  try {
    return normalizeCols(localStorage.getItem(LS_PREVIEW_COLS));
  } catch {
    return 1;
  }
}

/**
 * chat-doc-preview-rail (2026-08-11) — 확대/축소 + 썸네일 레일(펼침·좌우 배치).
 *
 * zoom 은 '자동 맞춤(=100%) 대비' 배율이라 100 이 기본.
 *
 * 레일은 **항상 접힌 채·좌측으로 시작한다** — 펼침 여부도 배치도 저장하지 않는다(2026-08-11
 * 사용자 요청). 한 번 펼치거나 오른쪽으로 옮겨 두면 다른 문서·다른 화면에서 열 때도 그대로
 * 따라와 본문이 좁아 보였다. 둘 다 '그 문서를 보는 동안만' 유효한 상태로 둔다.
 */
const LS_PREVIEW_ZOOM = 'chat.preview.zoom';

export type PreviewRailSide = 'left' | 'right';

function readZoom(): number {
  try {
    const v = Number(localStorage.getItem(LS_PREVIEW_ZOOM));
    return Number.isFinite(v) && v > 0 ? clampZoom(v) : PREVIEW_ZOOM_DEFAULT;
  } catch {
    return PREVIEW_ZOOM_DEFAULT;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage 비활성 — 무시(세션 내 동작 유지) */
  }
}

interface ChatPreviewState {
  open: OpenPreview | null;
  /** 미리보기 패널 가로 폭(px). 좌측 경계 드래그로 조절, localStorage 영속. */
  width: number;
  /** chat-doc-preview-fullscreen (2026-07-02) — 전체보기(전체화면) 토글. 닫힐 때 자동 해제. */
  fullscreen: boolean;
  /** 한 줄에 표시할 페이지 수(1/2/4). pdf·pptx 적용. */
  cols: ChatPreviewCols;
  /** 사용자 확대/축소(%). 100 = 자동 맞춤 그대로, 50~300. */
  zoom: number;
  /** 썸네일 레일 펼침 여부. 항상 닫힘으로 시작하며 저장하지 않는다(문서를 열 때마다 초기화). */
  railOpen: boolean;
  /** 썸네일 레일 위치. 항상 좌측으로 시작하며 저장하지 않는다(문서를 열 때마다 초기화). */
  railSide: PreviewRailSide;
  openPreview: (roomId: string, doc: PreviewDoc) => void;
  closePreview: () => void;
  /** 활성 방이 바뀌면 다른 방의 미리보기를 닫는다. */
  closeIfNotRoom: (roomId: string | null) => void;
  setWidth: (w: number) => void;
  toggleFullscreen: () => void;
  setFullscreen: (v: boolean) => void;
  /** 한 줄 페이지 수 설정(1/2/4). 목록 밖 값은 1 로 정규화 + localStorage 영속. */
  setCols: (v: number) => void;
  /** 확대/축소 설정(%). 50~300 clamp + 영속. */
  setZoom: (v: number) => void;
  /** 현재 배율에서 step 단위 증감 — 헤더 −/+ 버튼용. */
  stepZoom: (dir: 1 | -1) => void;
  /** 자동 맞춤(100%)으로 복귀. */
  resetZoom: () => void;
  toggleRail: () => void;
  setRailSide: (side: PreviewRailSide) => void;
}

export const useChatPreviewStore = create<ChatPreviewState>((set) => ({
  open: null,
  width: readPreviewWidth(),
  fullscreen: false,
  cols: readCols(),
  zoom: readZoom(),
  railOpen: false,
  railSide: 'left',
  // 새 문서를 열 때마다 레일은 '접힘 + 좌측' 으로 되돌린다(펼침·배치는 그 문서를 보는 동안만).
  openPreview: (roomId, doc) => set({ open: { roomId, doc }, railOpen: false, railSide: 'left' }),
  // 닫을 때 전체보기·레일 상태도 함께 해제(다음 열기는 도크 + 접힌 좌측 레일로 시작).
  closePreview: () => set({ open: null, fullscreen: false, railOpen: false, railSide: 'left' }),
  closeIfNotRoom: (roomId) =>
    set((s) =>
      s.open && s.open.roomId !== roomId
        ? { open: null, fullscreen: false, railOpen: false, railSide: 'left' as PreviewRailSide }
        : s,
    ),
  setWidth: (w) => {
    const clamped = clampPreviewWidth(w);
    try {
      localStorage.setItem(LS_PREVIEW_WIDTH, String(clamped));
    } catch {
      /* localStorage 비활성 — 무시(세션 내 동작 유지) */
    }
    set({ width: clamped });
  },
  toggleFullscreen: () => set((s) => ({ fullscreen: !s.fullscreen })),
  setFullscreen: (v) => set({ fullscreen: v }),
  setCols: (v) => {
    const next = normalizeCols(v);
    persist(LS_PREVIEW_COLS, String(next));
    set({ cols: next });
  },
  setZoom: (v) => {
    const next = clampZoom(v);
    persist(LS_PREVIEW_ZOOM, String(next));
    set({ zoom: next });
  },
  // set 함수형으로 현재값을 읽는다 — 연타 시 stale closure 로 같은 값이 두 번 적용되는 걸 방지.
  stepZoom: (dir) =>
    set((s) => {
      const next = clampZoom(s.zoom + dir * PREVIEW_ZOOM_STEP);
      persist(LS_PREVIEW_ZOOM, String(next));
      return { zoom: next };
    }),
  resetZoom: () => {
    persist(LS_PREVIEW_ZOOM, String(PREVIEW_ZOOM_DEFAULT));
    set({ zoom: PREVIEW_ZOOM_DEFAULT });
  },
  // 저장하지 않는다 — 이 문서를 보는 동안만 유효(다시 열면 접힘).
  toggleRail: () => set((s) => ({ railOpen: !s.railOpen })),
  // 저장하지 않는다 — 펼침과 마찬가지로 이 문서를 보는 동안만 유효(다시 열면 좌측).
  setRailSide: (side) => set({ railSide: side === 'right' ? 'right' : 'left' }),
}));

/**
 * 전체보기 전용 보기옵션 (2026-08-12 사용자 요청) — 한 줄 N장 · 확대/축소 · 썸네일 레일은
 * **전체보기에서만** 노출한다. 도크(우측 패널) 상태에서는 조작 수단이 없으므로 저장된 값이
 * 남아 있어도 화면은 항상 기본값(1장 · 100%)으로 그린다. 저장값(cols/zoom) 자체는 건드리지
 * 않아 전체보기로 되돌아가면 쓰던 값이 그대로 복원된다.
 */
export function useEffectivePreviewCols(): number {
  return useChatPreviewStore((s) => (s.fullscreen ? s.cols : 1));
}

export function useEffectivePreviewZoom(): number {
  return useChatPreviewStore((s) => (s.fullscreen ? s.zoom : PREVIEW_ZOOM_DEFAULT));
}
